import { useEffect, useState } from 'react'

import { Button } from 'components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Textarea } from 'components/ui/textarea'

import * as presentationService from '../services/presentationService'
import type { Story } from '../types/presentation.types'

/**
 * Save a story as a template (MYD-505), from the story's menu.
 *
 * Its slides and settings travel; its asset does not, so the template makes
 * a story about whichever asset of the same kind it is used for next. A block
 * that pointed at a different asset draws on the new story's asset instead.
 */
interface SaveTemplateDialogProps {
	open: boolean
	story: Story | null
	onClose: () => void
	onSaved: (name: string) => void
}

export function SaveTemplateDialog({ open, story, onClose, onSaved }: SaveTemplateDialogProps) {
	const [name, setName] = useState('')
	const [description, setDescription] = useState('')
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		if (!open) return
		setName(story?.title ? `${story.title} template` : '')
		setDescription('')
		setError(null)
	}, [open, story])

	const trimmed = name.trim()
	const blocked = !story?.anchor
		? 'Choose what this story is about first: a template is for one kind of asset.'
		: !story.slides.length
			? 'Add a slide first: a template needs at least one.'
			: null

	const submit = async () => {
		if (!story || !trimmed || blocked) return
		setBusy(true)
		setError(null)
		try {
			await presentationService.saveStoryAsTemplate({ name: trimmed, description: description.trim(), story })
			onSaved(trimmed)
		} catch (cause: any) {
			setError(cause?.response?.data?.message ?? 'The template could not be saved.')
		} finally {
			setBusy(false)
		}
	}

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent className="sm:max-w-[480px]">
				<DialogHeader>
					<DialogTitle>Save story as template</DialogTitle>
					<DialogDescription>Its slides become a template you can use for any {story?.anchor?.scope ?? 'asset'} of the same kind. Only you see it.</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 px-5 py-4">
					{blocked ? (
						<p className="text-sm text-amber-700">{blocked}</p>
					) : (
						<>
							<div className="space-y-2">
								<Label htmlFor="template-name">Name</Label>
								<Input id="template-name" value={name} maxLength={140} autoFocus onChange={event => setName(event.target.value)} />
							</div>
							<div className="space-y-2">
								<Label htmlFor="template-description">
									Description <span className="font-normal text-muted-foreground">(optional)</span>
								</Label>
								<Textarea id="template-description" rows={2} value={description} maxLength={500} onChange={event => setDescription(event.target.value)} />
							</div>
						</>
					)}
					{error && <p className="text-sm text-destructive">{error}</p>}
				</div>

				<DialogFooter>
					<Button variant="ghost" isOutline onClick={onClose} disabled={busy}>
						Cancel
					</Button>
					<Button onClick={submit} disabled={busy || !trimmed || Boolean(blocked)}>
						{busy ? 'Saving...' : 'Save template'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default SaveTemplateDialog
